import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { RegionProvider } from './context/RegionContext';
import { Toaster } from './components/ui/sonner';
import { RegionBanner } from './components/RegionBanner';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';

// Native app initialization (Capacitor)
import { initializeApp, initializePushNotifications, isNative } from './utils/native';

// Initialize native features when app loads
if (isNative) {
  initializeApp().then(() => {
    console.log('[BodyCorp365] Native app initialized');
  });
}

// Register Firebase service worker (web only)
if (!isNative && 'serviceWorker' in navigator) {
  navigator.serviceWorker.register('/firebase-messaging-sw.js')
    .then((registration) => {
      console.log('Firebase SW registered:', registration.scope);
    })
    .catch((error) => {
      console.log('Firebase SW registration failed:', error);
    });
}

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import SelectRole from './pages/SelectRole';
import ResidentDashboard from './pages/ResidentDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminNotices from './pages/AdminNotices';
import AdminMaintenance from './pages/AdminMaintenance';
import AdminResidents from './pages/AdminResidents';
import AdminFinancials from './pages/AdminFinancials';
import AdminLevyBilling from './pages/AdminLevyBilling';
import AdminContractors from './pages/AdminContractors';
import MaintenancePage from './pages/MaintenancePage';
import NoticesPage from './pages/NoticesPage';
import EventsPage from './pages/EventsPage';
import ProfilePage from './pages/ProfilePage';
import DocumentVault from './pages/DocumentVault';
import FacilityBooking from './pages/FacilityBooking';
import VisitorManagement from './pages/VisitorManagement';
import ParcelTracking from './pages/ParcelTracking';
import Marketplace from './pages/Marketplace';
import LevyPayments from './pages/LevyPayments';
import Voting from './pages/Voting';
import AdminVoting from './pages/AdminVoting';
import CommunityChat from './pages/CommunityChat';
import ServiceProviders from './pages/ServiceProviders';
import SettingsPage from './pages/SettingsPage';

// Managing Agent & Platform Owner Pages
import ManagingAgentDashboard from './pages/ManagingAgentDashboard';
import PlatformOwnerDashboard from './pages/PlatformOwnerDashboard';
import CompanyRegistration from './pages/CompanyRegistration';
import CompanyActivation from './pages/CompanyActivation';
import AgentRequestAccess from './pages/AgentRequestAccess';
import AgentRequests from './pages/AgentRequests';
import FirstLoginPasswordChange from './pages/FirstLoginPasswordChange';
import AgentBuildingWorkspace from './pages/AgentBuildingWorkspace';
import AgentFinancials from './pages/AgentFinancials';
import AgentTickets from './pages/AgentTickets';
import AgentSales from './pages/AgentSales';
import ContractorPortal from './pages/ContractorPortal';
import TrusteeHub from './pages/TrusteeHub';
import AgentReports from './pages/AgentReports';
import ComplianceDashboard from './pages/ComplianceDashboard';
import WhiteLabelBranding from './pages/WhiteLabelBranding';
import InsuranceClaims from './pages/InsuranceClaims';
import AdminInsuranceClaims from './pages/AdminInsuranceClaims';
import InsuranceVault from './pages/InsuranceVault';
import SignupPricing from './pages/SignupPricing';
import SignupSuccess from './pages/SignupSuccess';
import SignupCancelled from './pages/SignupCancelled';
import AgentStaff from './pages/AgentStaff';
import AgentSettings from './pages/AgentSettings';
import Governance from './pages/Governance';
import Operations from './pages/Operations';
import FinancialDashboard from './pages/FinancialDashboard';
import PartnerDashboard from './pages/PartnerDashboard';
import AdminPartnerProgram from './pages/AdminPartnerProgram';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PricingPage from './pages/PricingPage';
import TermsOfService from './pages/TermsOfService';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import SuperAgentDashboard from './pages/SuperAgentDashboard';
import SuperAgentReports from './pages/SuperAgentReports';
import TestPayment from './pages/TestPayment';
import RenewSubscription from './pages/RenewSubscription';
import AgentSubscriptions from './pages/AgentSubscriptions';
import ResidentManagement from './pages/ResidentManagement';
import InvitationRegister from './pages/InvitationRegister';
import ResidentBookings from './pages/ResidentBookings';
import ResidentVenueBookings from './pages/ResidentVenueBookings';
import AppHome from './pages/AppHome';
import BuildingActivate from './pages/BuildingActivate';
import AgentBookingDashboard from './pages/AgentBookingDashboard';
import AgentVenueBookings from './pages/AgentVenueBookings';
import ResidentCalendar from './pages/ResidentCalendar';
import BuildingPayments from './pages/BuildingPayments';
import PlatformOwnerPaymentDashboard from './pages/PlatformOwnerPaymentDashboard';
import PaymentResult from './pages/PaymentResult';
import DigitalBuildingRecord from './pages/DigitalBuildingRecord';
import ComplianceAlerts from './pages/ComplianceAlerts';
import AgentInvitations from './pages/AgentInvitations';
import TrialRegistration from './pages/TrialRegistration';
import Subscribe from './pages/Subscribe';
import BuildingOnboarding from './pages/BuildingOnboarding';
import CreateBuilding from './pages/CreateBuilding';
import UserManagement from './pages/UserManagement';
import AuditLog from './pages/AuditLog';
import PlatformBuildings from './pages/PlatformBuildings';
import PlatformBuildingProfile from './pages/PlatformBuildingProfile';
import PlatformCommunications from './pages/PlatformCommunications';
import MyBuildings from './pages/MyBuildings';
import Notifications from './pages/Notifications';
import InviteManagingAgent from './pages/InviteManagingAgent';
import CompanyInvitations from './pages/CompanyInvitations';
import InviteAcceptPublic from './pages/InviteAcceptPublic';
import BoDyAssistant from './components/BoDyAssistant';
import PlatformGate from './components/PlatformGate';
import ConsentGate from './components/ConsentGate';
import ConsentLog from './pages/ConsentLog';
import AgentNominations from './pages/AgentNominations';
import WelcomeBuilding from './pages/WelcomeBuilding';
import BoDyKnowledge from './pages/BoDyKnowledge';
import ActiveBuildingBanner from './components/ActiveBuildingBanner';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ConfirmEmailChange from './pages/ConfirmEmailChange';
import { TrialBanner } from './components/TrialBanner';
import { isNative as isNativeApp } from './utils/native';

// Protected Route Component with role-based redirects and account status check
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, accountStatus } = useAuth();
  const location = useLocation();
  const native = isNativeApp;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force role selection for brand-new users. This is the LAST gate — after POPIA.
  if (user.role_selected === false && location.pathname !== '/select-role') {
    return <Navigate to="/select-role" replace />;
  }

  // Effective role honours dual-role users: if they've toggled to a secondary
  // role via the header switcher, treat that role as active for routing.
  const secondary = Array.isArray(user.secondary_roles) ? user.secondary_roles : [];
  const availableRoles = [user.role, ...secondary].filter(Boolean);
  let activeRoleView = null;
  try { activeRoleView = localStorage.getItem('active_role_view'); } catch { activeRoleView = null; }
  const effectiveRole = availableRoles.includes(activeRoleView) ? activeRoleView : user.role;

  const hasAnyRole = (roles) => roles.some((r) => availableRoles.includes(r));

  // Check for frozen/expired account - redirect to renewal page
  // Skip for certain paths and roles
  const exemptPaths = ['/renew-subscription', '/renewal', '/logout', '/dashboard/settings'];
  const exemptRoles = ['platform_owner', 'managing_agent', 'agent_admin'];
  
  if (accountStatus?.is_frozen && 
      !exemptPaths.some(p => location.pathname.startsWith(p)) &&
      !exemptRoles.includes(user.role)) {
    return <Navigate to="/renew-subscription" replace />;
  }

  // Always redirect agents to their appropriate dashboard if they land on /dashboard
  // (skipped on native app — every signed-in user uses the resident dashboard on mobile)
  if (!native && location.pathname === '/dashboard' && ['managing_agent', 'agent_admin'].includes(effectiveRole)) {
    return <Navigate to="/agent" replace />;
  }
  if (!native && location.pathname === '/dashboard' && effectiveRole === 'platform_owner') {
    return <Navigate to="/platform" replace />;
  }
  if (!native && location.pathname === '/dashboard' && ['property_manager', 'trustee'].includes(effectiveRole)) {
    return <Navigate to="/admin" replace />;
  }

  if (allowedRoles && !hasAnyRole(allowedRoles)) {
    // On native, force everyone onto /dashboard regardless of role
    if (native) {
      // Allow the resident dashboard for any role on native
      if (location.pathname === '/dashboard' || allowedRoles.includes('resident')) {
        return children;
      }
      return <Navigate to="/dashboard" replace />;
    }
    // Web: redirect to role-appropriate dashboard
    if (effectiveRole === 'platform_owner') {
      return <Navigate to="/platform" replace />;
    }
    if (['managing_agent', 'agent_admin'].includes(effectiveRole)) {
      return <Navigate to="/agent" replace />;
    }
    if (['property_manager', 'trustee'].includes(effectiveRole)) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/app-home" element={<AppHome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/invitation-register" element={<InvitationRegister />} />
      <Route path="/signup" element={<SignupPricing />} />
      <Route path="/signup/success" element={<SignupSuccess />} />
      <Route path="/signup/cancelled" element={<SignupCancelled />} />
      <Route path="/pricing" element={<SignupPricing />} />
      <Route path="/test-payment" element={<TestPayment />} />
      <Route path="/test-payment/success" element={<TestPayment />} />
      <Route path="/test-payment/cancelled" element={<TestPayment />} />
      <Route path="/renew-subscription" element={<RenewSubscription />} />
      <Route path="/renewal/success" element={<RenewSubscription />} />
      <Route path="/renewal/cancelled" element={<RenewSubscription />} />
      <Route path="/payment-success" element={<PaymentResult />} />
      <Route path="/payment-cancelled" element={<PaymentResult />} />
      <Route path="/agent-signup" element={<CompanyRegistration />} />
      <Route path="/company-signup" element={<CompanyRegistration />} />
      <Route path="/register-company" element={<CompanyRegistration />} />
      <Route path="/activate-company" element={<CompanyActivation />} />
      <Route path="/agent-request-access" element={<AgentRequestAccess />} />
      <Route path="/agent/signup" element={<AgentRequestAccess />} />
      <Route path="/first-login-password-change" element={<ProtectedRoute><FirstLoginPasswordChange /></ProtectedRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/how-it-works" element={<AboutUs />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/pricing-info" element={<PricingPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/free-trial" element={<TrialRegistration />} />
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/building-onboarding" element={<BuildingOnboarding />} />
      <Route path="/find-building" element={<BuildingOnboarding />} />
      <Route path="/select-role" element={<ProtectedRoute><SelectRole /></ProtectedRoute>} />
      <Route path="/welcome-building/:hoaId" element={<WelcomeBuilding />} />
      <Route path="/create-building" element={<CreateBuilding />} />
      <Route path="/building/:hoaId/activate" element={<ProtectedRoute><BuildingActivate /></ProtectedRoute>} />
      <Route path="/join/:inviteCode" element={<BuildingOnboarding />} />

      {/* Resident Routes */}
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['resident']}><ResidentDashboard /></ProtectedRoute>} />
      <Route path="/dashboard/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
      <Route path="/dashboard/maintenance/new" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
      <Route path="/dashboard/notices" element={<ProtectedRoute><NoticesPage /></ProtectedRoute>} />
      <Route path="/dashboard/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      <Route path="/dashboard/calendar" element={<ProtectedRoute><ResidentCalendar /></ProtectedRoute>} />
      <Route path="/dashboard/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/dashboard/documents" element={<ProtectedRoute><DocumentVault /></ProtectedRoute>} />
      <Route path="/dashboard/facilities" element={<ProtectedRoute><ResidentBookings /></ProtectedRoute>} />
      <Route path="/dashboard/bookings" element={<ProtectedRoute><ResidentBookings /></ProtectedRoute>} />
      <Route path="/dashboard/venue-bookings" element={<ProtectedRoute><ResidentVenueBookings /></ProtectedRoute>} />
      <Route path="/dashboard/visitors" element={<ProtectedRoute><VisitorManagement /></ProtectedRoute>} />
      <Route path="/dashboard/parcels" element={<ProtectedRoute><ParcelTracking /></ProtectedRoute>} />
      <Route path="/dashboard/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
      <Route path="/dashboard/payments" element={<ProtectedRoute><LevyPayments /></ProtectedRoute>} />
      <Route path="/dashboard/voting" element={<ProtectedRoute><Voting /></ProtectedRoute>} />
      <Route path="/dashboard/chat" element={<ProtectedRoute><CommunityChat /></ProtectedRoute>} />
      <Route path="/dashboard/providers" element={<ProtectedRoute><ServiceProviders /></ProtectedRoute>} />
      <Route path="/dashboard/financial-dashboard" element={<ProtectedRoute><FinancialDashboard /></ProtectedRoute>} />

      {/* Admin Routes (Trustees, Property Managers) */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/notices" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminNotices /></ProtectedRoute>} />
      <Route path="/admin/maintenance" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminMaintenance /></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><DocumentVault /></ProtectedRoute>} />
      <Route path="/admin/financials" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminFinancials /></ProtectedRoute>} />
      <Route path="/admin/financial-dashboard" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><FinancialDashboard /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/admin/events" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><EventsPage /></ProtectedRoute>} />
      <Route path="/admin/residents" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminResidents /></ProtectedRoute>} />
      <Route path="/admin/units" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminResidents /></ProtectedRoute>} />
      <Route path="/admin/providers" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><ServiceProviders /></ProtectedRoute>} />
      <Route path="/admin/contractors" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminContractors /></ProtectedRoute>} />
      <Route path="/admin/voting" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner', 'managing_agent', 'agent_admin']}><AdminVoting /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminDashboard /></ProtectedRoute>} />

      {/* User Management & Audit Log */}
      <Route path="/admin/user-management" element={<ProtectedRoute allowedRoles={['trustee', 'chairman', 'property_manager', 'managing_agent', 'agent_admin', 'super_agent', 'general_agent', 'platform_owner']}><UserManagement /></ProtectedRoute>} />
      <Route path="/agent/user-management" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'super_agent', 'general_agent', 'platform_owner']}><UserManagement /></ProtectedRoute>} />
      <Route path="/platform/user-management" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><UserManagement /></PlatformGate></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute allowedRoles={['trustee', 'chairman', 'property_manager', 'managing_agent', 'agent_admin', 'super_agent', 'general_agent', 'platform_owner']}><AuditLog /></ProtectedRoute>} />
      <Route path="/platform/audit-log" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><AuditLog /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/buildings" element={<ProtectedRoute allowedRoles={['platform_owner', 'super_agent', 'agent_admin']}><PlatformGate><PlatformBuildings /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/buildings/:hoaId" element={<ProtectedRoute allowedRoles={['platform_owner', 'super_agent', 'agent_admin']}><PlatformGate><PlatformBuildingProfile /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/communications" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><PlatformCommunications /></PlatformGate></ProtectedRoute>} />
      <Route path="/my-buildings" element={<ProtectedRoute><MyBuildings /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/admin/buildings/:hoaId/invite-agent" element={<ProtectedRoute allowedRoles={['trustee', 'chairman', 'property_manager', 'platform_owner']}><InviteManagingAgent /></ProtectedRoute>} />
      <Route path="/agent/building-invitations" element={<ProtectedRoute allowedRoles={['super_agent', 'agent_admin', 'managing_agent', 'platform_owner']}><CompanyInvitations /></ProtectedRoute>} />
      <Route path="/invite/:token" element={<InviteAcceptPublic />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
      <Route path="/platform/bo-dy/knowledge" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><BoDyKnowledge /></PlatformGate></ProtectedRoute>} />

      {/* Contractor Portal (Public with ID) */}
      <Route path="/contractor-portal" element={<ContractorPortal />} />

      {/* Settings Page - accessible to all logged in users */}
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

      {/* Trustee Hub - For Trustees and Managing Agents */}
      <Route path="/trustee-hub" element={<ProtectedRoute allowedRoles={['trustee', 'managing_agent', 'agent_admin', 'property_manager', 'platform_owner']}><TrusteeHub /></ProtectedRoute>} />
      <Route path="/admin/trustee-hub" element={<ProtectedRoute allowedRoles={['trustee', 'managing_agent', 'agent_admin', 'property_manager', 'platform_owner']}><TrusteeHub /></ProtectedRoute>} />

      {/* Resident Management - For Trustees and Managing Agents */}
      <Route path="/admin/residents" element={<ProtectedRoute allowedRoles={['trustee', 'chairman', 'managing_agent', 'super_agent', 'general_agent', 'property_manager', 'platform_owner']}><ResidentManagement /></ProtectedRoute>} />

      {/* Governance & Resolutions - For Trustees and Managing Agents */}
      <Route path="/governance" element={<ProtectedRoute allowedRoles={['trustee', 'managing_agent', 'agent_admin', 'property_manager', 'platform_owner']}><Governance /></ProtectedRoute>} />

      {/* Operations Management - For Trustees and Managing Agents */}
      <Route path="/operations" element={<ProtectedRoute allowedRoles={['trustee', 'managing_agent', 'agent_admin', 'property_manager', 'platform_owner']}><Operations /></ProtectedRoute>} />

      {/* Insurance Claims Routes */}
      <Route path="/dashboard/insurance" element={<ProtectedRoute><InsuranceClaims /></ProtectedRoute>} />
      <Route path="/admin/insurance" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><AdminInsuranceClaims /></ProtectedRoute>} />
      <Route path="/admin/insurance/:hoaId" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'managing_agent', 'agent_admin', 'platform_owner']}><AdminInsuranceClaims /></ProtectedRoute>} />
      <Route path="/admin/insurance-vault" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'platform_owner']}><InsuranceVault /></ProtectedRoute>} />
      <Route path="/admin/insurance-vault/:hoaId" element={<ProtectedRoute allowedRoles={['property_manager', 'trustee', 'managing_agent', 'agent_admin', 'platform_owner']}><InsuranceVault /></ProtectedRoute>} />
      <Route path="/agent/insurance/:hoaId" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AdminInsuranceClaims /></ProtectedRoute>} />
      <Route path="/agent/insurance-vault/:hoaId" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><InsuranceVault /></ProtectedRoute>} />

      {/* Managing Agent Routes */}
      <Route path="/agent" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><ManagingAgentDashboard /></ProtectedRoute>} />
      <Route path="/agent/company-management" element={<ProtectedRoute allowedRoles={['agent_admin', 'platform_owner']}><SuperAgentDashboard /></ProtectedRoute>} />
      <Route path="/agent/super-reports" element={<ProtectedRoute allowedRoles={['agent_admin', 'platform_owner']}><SuperAgentReports /></ProtectedRoute>} />
      <Route path="/agent/partner" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin']}><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentBuildingWorkspace /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/financials" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentFinancials /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/financial-dashboard" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><FinancialDashboard /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/analytics" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/tickets" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentTickets /></ProtectedRoute>} />
      <Route path="/agent/reports" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentReports /></ProtectedRoute>} />
      <Route path="/agent/compliance" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><ComplianceDashboard /></ProtectedRoute>} />
      <Route path="/agent/compliance/:hoaId" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><ComplianceDashboard /></ProtectedRoute>} />
      <Route path="/agent/branding" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><WhiteLabelBranding /></ProtectedRoute>} />
      <Route path="/agent/staff" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentStaff /></ProtectedRoute>} />
      <Route path="/agent/settings" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentSettings /></ProtectedRoute>} />
      <Route path="/agent/sales" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentSales /></ProtectedRoute>} />
      <Route path="/agent/subscriptions" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentSubscriptions /></ProtectedRoute>} />
      <Route path="/agent/bookings" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner', 'trustee', 'property_manager']}><AgentBookingDashboard /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/bookings" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentBookingDashboard /></ProtectedRoute>} />
      <Route path="/agent/venue-bookings" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'super_agent', 'platform_owner', 'trustee', 'property_manager']}><AgentVenueBookings /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/venue-bookings" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'super_agent', 'platform_owner', 'trustee', 'property_manager']}><AgentVenueBookings /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/documents" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><DocumentVault /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/notices" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AdminNotices /></ProtectedRoute>} />
      <Route path="/agent/building-payments" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><BuildingPayments /></ProtectedRoute>} />
      <Route path="/agent/building/:hoaId/record" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner', 'trustee']}><DigitalBuildingRecord /></ProtectedRoute>} />
      <Route path="/agent/compliance-alerts" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><ComplianceAlerts /></ProtectedRoute>} />
      <Route path="/agent/invitations" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><AgentInvitations /></ProtectedRoute>} />
      <Route path="/agent/agent-requests" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'super_agent', 'platform_owner']}><AgentRequests /></ProtectedRoute>} />
      <Route path="/agent/*" element={<ProtectedRoute allowedRoles={['managing_agent', 'agent_admin', 'platform_owner']}><ManagingAgentDashboard /></ProtectedRoute>} />

      {/* Platform Owner Routes */}
      <Route path="/platform" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><PlatformOwnerDashboard /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/partners" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><AdminPartnerProgram /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/payments" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><PlatformOwnerPaymentDashboard /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/building/:hoaId/record" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><DigitalBuildingRecord /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/compliance-alerts" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><ComplianceAlerts /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/consent-log" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><ConsentLog /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/agent-nominations" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><AgentNominations /></PlatformGate></ProtectedRoute>} />
      <Route path="/platform/*" element={<ProtectedRoute allowedRoles={['platform_owner']}><PlatformGate><PlatformOwnerDashboard /></PlatformGate></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RegionProvider>
          <WebSocketProvider>
            <TrialBanner />
            <ConsentGate>
              <AppRouter />
            </ConsentGate>
            <ActiveBuildingBanner />
            <BoDyAssistant />
            <RegionBanner />
            <Toaster position="top-center" richColors />
          </WebSocketProvider>
        </RegionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
